import React from 'react';
import AnimatedWrapper from '../AnimatedWrapper';
import { CheckCircleIcon } from '../icons/CheckCircleIcon';

const SellersGuidePage: React.FC = () => {
    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center text-white overflow-hidden">
                <div className="absolute inset-0">
                    <img loading="lazy" src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=2070&auto=format&fit=crop"
                        alt="Real Estate Calicut trusted property marketplace"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <AnimatedWrapper>
                        <h1 className="text-4xl sm:text-6xl font-bold tracking-tight mb-6 leading-tight">
                            Sell or Rent <br />With Confidence
                        </h1>
                        <p className="mt-4 text-xl sm:text-2xl text-gray-100 max-w-3xl mx-auto font-light">
                            Connect directly with genuine prospects while staying in full control of your property.
                        </p>
                    </AnimatedWrapper>
                </div>
            </section>

            {/* Intro Section - ZigZag */}
            <section className="py-16 sm:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <AnimatedWrapper>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Welcome Owners</h2>
                            <p className="text-lg text-gray-600 leading-relaxed mb-6">
                                Finding serious tenants or buyers without wasting time on random calls and unreliable brokers is a big challenge for building and property owners in Kerala.
                            </p>
                            <p className="text-lg text-gray-600 leading-relaxed mb-8">
                                <span className="font-semibold text-brand-primary">SellAny.in</span> helps you market your property professionally and connect directly with genuine prospects while you stay in full control of decisions and pricing.
                            </p>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={200}>
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img loading="lazy" src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=1932&auto=format&fit=crop"
                                    alt="Business Deal"
                                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* How It Works - Cards */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimatedWrapper>
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900">How SellAny Works for Owners</h2>
                            <p className="mt-4 text-gray-500 text-lg">A streamlined process to get the best value for your property.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                                    <span className="text-brand-primary font-bold text-xl">1</span>
                                </div>
                                <h3 className="font-bold text-xl text-gray-900 mb-3">Sign & List</h3>
                                <p className="text-gray-600 leading-relaxed">You sign a simple marketing agreement allowing us to promote your property via banners and digital platforms.</p>
                            </div>
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                                    <span className="text-brand-primary font-bold text-xl">2</span>
                                </div>
                                <h3 className="font-bold text-xl text-gray-900 mb-3">We Handle The Rest</h3>
                                <p className="text-gray-600 leading-relaxed">Our team manages photos, listings, and enquiries, filtering serious prospects and arranging visits at your convenience.</p>
                            </div>
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                                    <span className="text-brand-primary font-bold text-xl">3</span>
                                </div>
                                <h3 className="font-bold text-xl text-gray-900 mb-3">Pay on Success</h3>
                                <p className="text-gray-600 leading-relaxed">Once a deal is successfully closed, you pay the agreed service fee (e.g., one month’s rent).</p>
                            </div>
                        </div>
                    </AnimatedWrapper>
                </div>
            </section>

            {/* Preparing Property - ZigZag Reversed */}
            <section className="py-16 sm:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <AnimatedWrapper delay={200} className="order-2 lg:order-1">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px]">
                                <img loading="lazy" src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2150&auto=format&fit=crop"
                                    alt="Clean Interior"
                                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                        </AnimatedWrapper>
                        <AnimatedWrapper className="order-1 lg:order-2">
                            <h2 className="text-3xl font-bold text-gray-900 mb-8">Preparing Your Property</h2>
                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <CheckCircleIcon className="w-6 h-6 text-brand-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg text-gray-900">Make it Presentable</h3>
                                        <p className="text-gray-600 mt-1">Cleaning, basic repairs, working lights, and neat common areas can significantly improve response and achievable rent/price.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <CheckCircleIcon className="w-6 h-6 text-brand-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg text-gray-900">Share Clear Details</h3>
                                        <p className="text-gray-600 mt-1">Provide exact location, usable area, floor, facing, water situation, parking, age of building, and association rules.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <CheckCircleIcon className="w-6 h-6 text-brand-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg text-gray-900">Provide Documents</h3>
                                        <p className="text-gray-600 mt-1">Have basic documents ready for verification and smoother closure, such as ownership proof and tax receipts.</p>
                                    </div>
                                </div>
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* During Enquiries - Background Section */}
            <section className="py-16 sm:py-24 bg-gray-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img loading="lazy" src="https://www.transparenttextures.com/patterns/cubes.png" alt="Overlay" className="w-full h-full object-cover" />
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <AnimatedWrapper>
                            <h2 className="text-3xl font-bold mb-6">During Enquiries & Visits</h2>
                            <p className="text-gray-300 text-lg mb-8">Professional handling to find the right match.</p>

                            <ul className="space-y-6">
                                <li className="flex items-start bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                                    <span className="font-bold text-xl mr-4 text-brand-primary">01</span>
                                    <div>
                                        <h4 className="font-semibold text-lg">Be Honest & Consistent</h4>
                                        <p className="text-gray-300 text-sm mt-1">Clearly state price, deposit, and inclusions (parking, furniture) to avoid wasting time.</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                                    <span className="font-bold text-xl mr-4 text-brand-primary">02</span>
                                    <div>
                                        <h4 className="font-semibold text-lg">Fix Viewing Slots</h4>
                                        <p className="text-gray-300 text-sm mt-1">Avoid disturbance; our team coordinates slots so only genuine prospects visit.</p>
                                    </div>
                                </li>
                                <li className="flex items-start bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                                    <span className="font-bold text-xl mr-4 text-brand-primary">03</span>
                                    <div>
                                        <h4 className="font-semibold text-lg">Listen to Feedback</h4>
                                        <p className="text-gray-300 text-sm mt-1">Constructive market feedback on pricing or small improvements can help close deals faster.</p>
                                    </div>
                                </li>
                            </ul>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={200}>
                            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                                <img loading="lazy" src="https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=2070&auto=format&fit=crop"
                                    alt="Discussion"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* Documentation & Safety */}
            <section className="py-16 sm:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <AnimatedWrapper>
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold text-gray-900">Documentation & Legal Safety</h2>
                            <p className="mt-4 text-gray-500 text-lg">Protecting your asset and your interests.</p>
                        </div>
                    </AnimatedWrapper>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <AnimatedWrapper delay={100}>
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-brand-primary h-full">
                                <h3 className="font-bold text-xl text-gray-900 mb-4">Written Agreement</h3>
                                <p className="text-gray-600">Always use a proper written agreement with names, ID details, property description, and clear conditions.</p>
                            </div>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={200}>
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-brand-primary h-full">
                                <h3 className="font-bold text-xl text-gray-900 mb-4">Legal Protection</h3>
                                <p className="text-gray-600">Respect laws while protecting your interests. Use clear clauses drafted with legal help if needed.</p>
                            </div>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={300}>
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-brand-primary h-full">
                                <h3 className="font-bold text-xl text-gray-900 mb-4">Payment Proof</h3>
                                <p className="text-gray-600">Keep proof of all payments received and refunds given to avoid disputes and aid tax compliance.</p>
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* How SellAny Supports Owners */}

        </div>
    );
};

export default SellersGuidePage;
